import type {
  CandidateAccount,
  FullCandidateAccount,
  Taxonomy,
} from '@axiom/validation';
import { TaxonomyUtil } from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';
import type { Moment } from 'moment';

import { DateUtil } from '../../utils/date-util';

import { MyBenchConst } from './my-bench.const';
import type { MyBenchSortFiltersForm } from './my-bench-sort-filters-form-schema';

const { Engaged, Completed } = CandidateOpportunitiesConst.CandidateStatuses;

export const MyBenchUtil = {
  filterAndSort: ({
    filterData,
    candidates,
    isEnvoyUser = false,
  }: {
    filterData: MyBenchSortFiltersForm;
    candidates: FullCandidateAccount[];
    isEnvoyUser: boolean;
  }): FullCandidateAccount[] => {
    let data = [...candidates];

    const filterItemPracticeArea =
      filterData[MyBenchConst.Filters.PRACTICEAREA];
    if (filterItemPracticeArea !== 'all') {
      data = data.filter(candidate => {
        return candidate.primaryPracticeArea === filterItemPracticeArea;
      });
    }

    const filterItemStatus = filterData[MyBenchConst.Filters.STATUS];
    if (filterItemStatus !== 'all' && filterItemStatus !== 'hidden') {
      data = data.filter(candidate => {
        return candidate.accountCandidateStatus === filterItemStatus;
      });
    } else if (isEnvoyUser && filterItemStatus === 'hidden') {
      data = data.filter(candidate => {
        return candidate.isHiddenExternal;
      });
    }

    const searchItem = filterData[MyBenchConst.Filters.SEARCH];
    if (searchItem) {
      data = data.filter(candidate =>
        candidate.searchableText.includes(searchItem.toLowerCase())
      );
    }

    const sortItem = filterData[MyBenchConst.Filters.SORT];

    if (sortItem !== null && sortItem !== 'default') {
      data = data.sort((candidateA, candidateB) => {
        if ((candidateA[sortItem] ?? '') > (candidateB[sortItem] ?? '')) {
          return 1;
        }
        if ((candidateA[sortItem] ?? '') < (candidateB[sortItem] ?? '')) {
          return -1;
        }
        if (
          (candidateA.calculatedDisplayName ?? '') >
          (candidateB.calculatedDisplayName ?? '')
        ) {
          return 1;
        }
        return (candidateA.calculatedDisplayName ?? '') <
          (candidateB.calculatedDisplayName ?? '')
          ? -1
          : 0;
      });
    }

    return data;
  },
  getFilterOptions: ({
    taxonomy,
    isEnvoyUser,
  }: {
    taxonomy: Taxonomy;
    isEnvoyUser: boolean;
  }) => {
    const statusOptions = [
      { label: 'All', value: 'all' },
      { label: 'Currently engaged', value: Engaged },
      { label: 'Previously engaged', value: Completed },
    ];

    if (isEnvoyUser) {
      statusOptions.push({
        label: 'Hidden',
        value: 'hidden',
      });
    }

    return {
      sort: [
        { label: 'Default', value: 'default' },
        {
          label: 'Additional availability',
          value: MyBenchConst.SortOptions.WEEKLYAVAILABILITY,
        },
        {
          label: 'Years of experience',
          value: MyBenchConst.SortOptions.YEARSOFEXPERIENCE,
        },
      ],
      practiceAreas: [
        { label: 'All', value: 'all' },
        ...TaxonomyUtil.getPracticeAreaOptions(taxonomy.practiceAreas),
      ],
      status: statusOptions,
    };
  },
  addSearchableText: ({
    candidates,
    taxonomy,
  }: {
    candidates: CandidateAccount[];
    taxonomy: Taxonomy;
  }): FullCandidateAccount[] => {
    return candidates.map(c => {
      const searchableText = [];

      searchableText.push(
        c.weeklyAvailability,
        c.billingUnitsPerWeek,
        c.weeklyTimeUnit
      );

      if (c.calculatedDisplayName) {
        searchableText.push(c.calculatedDisplayName);
      }

      if (c.calculatedNotes) {
        searchableText.push(c.calculatedNotes);
      }

      if (c.soonestEngagementEndDate) {
        const mDate = DateUtil.castAsMoment(
          c.soonestEngagementEndDate
        ) as Moment;
        MyBenchConst.SearchableDateFormats.forEach(format => {
          searchableText.push(mDate.format(format));
        });
      }

      (c.degrees ?? []).forEach(deg =>
        searchableText.push(`${deg.institution} ${deg.yearAwarded}`)
      );

      (c.barredLocations ?? []).forEach(b => {
        if (b.name) {
          searchableText.push(b.name);
        }
      });

      (c.industries ?? []).forEach(industry => {
        const data = taxonomy.industries[industry.industryValue];
        if (data) {
          searchableText.push(data.displayValue);
        }
      });

      if (c.primaryPracticeArea) {
        const practiceAreaObj = taxonomy.practiceAreas[c.primaryPracticeArea];
        if (practiceAreaObj) {
          searchableText.push(practiceAreaObj.fullName);
        }
      }

      if (c.lawSchool) {
        searchableText.push(c.lawSchool);
      }

      if (c.addressState) {
        searchableText.push(c.addressState);
      }

      if (c.addressCountry) {
        searchableText.push(c.addressCountry);
      }

      return {
        ...c,
        searchableText: searchableText.join(' ').toLowerCase(),
      };
    });
  },
};
